import { useEffect, useRef } from 'react'
import { AppProps } from 'next/app'
import Head from 'next/head'
import UserContextWrapper from 'context/common/User/UserContextWrapper'
import { OsanoCmpProviderSyncedToAuth } from 'context/common/OsanoCmpContext'
import { NextRouter } from 'next/router'
import { MODAL_PORTAL_ROOT_ID, sessionStorageKeys } from 'constants/common'
import 'react-toastify/dist/ReactToastify.css'
// test

//Redux
import { store } from 'state/store'

// All the global css files can be imported here
import 'theme/legacy/main.scss'
import '../globals.css'
import 'theme/legacy/_swiper.scss'
import { useRouter } from 'next/router'
import {
  captureFromCurrentUrl,
  reapplyMarketingParamsToUrl,
} from 'lib/marketingParams'
import { CALLRAIL_SWAP_SCRIPT_SRC } from 'lib/callrailScriptSrc'
import logPageView from 'utils/logPageView'
import { startSalesforceIframeLocationBridge } from 'utils/salesforceIframeLocationBridge'
import { ToastContainer } from 'react-toastify'
import useGetTreatment from 'hooks/treatments/useGetTreatment'
import { TreatmentFlags, treatmentFlagValues } from 'constants/treatments'
import AuthContextWrapper from 'context/AuthContext'
import 'theme/legacy/_typography_variables.scss'
import Datadog from 'app/DataDog'
import DatadogIdentity from 'app/DataDog/DatadogIdentity'
import ErrorFallback from 'common/ErrorFallback'
import useRudderStackInit from 'hooks/useRudderStackInit'
import useHeapInit from 'hooks/useHeapInit'
import useOsanoConsentEvents from 'hooks/useOsanoConsentEvents'
import useReloadOnConsentRevocation from 'hooks/useReloadOnConsentRevocation'
import Script from 'next/script'
import useToast from 'hooks/useToast'
import { QueryClient, QueryClientProvider } from 'react-query'
import RudderStackContext from 'context/common/RudderStackContext'
import { nextFontVariables } from 'component_library/fonts'
import { ReduxProviders } from 'state/ReduxProviders'
import { Toaster } from '@/base_components/ui/sonner'
import ProviderWaitingNotifications from 'components/common/ProviderWaitingNotifications'

const queryClient = new QueryClient()

// stores current router query object in browser's session, inorder to get that value as previous router query object
const storePreviousRouterQuery = (router: NextRouter): void => {
  const { pathname, query } = router
  const routerObj = { query, pathname }

  // Set the previous query obj as the value of the current query obj.
  const prevRouterObj = sessionStorage.getItem(
    sessionStorageKeys.CURRENT_ROUTER
  )
  sessionStorage.setItem(sessionStorageKeys.PREVIOUS_ROUTER, prevRouterObj)

  // Set the current query obj value by looking at the browser's router object.
  sessionStorage.setItem(
    sessionStorageKeys.CURRENT_ROUTER,
    JSON.stringify(routerObj)
  )
}

/**
 * Lives under OsanoCmpProviderSyncedToAuth so analytics consent tracks auth.
 */
function PagesAnalyticsShell({
  Component,
  pageProps,
}: Pick<AppProps, 'Component' | 'pageProps'>): JSX.Element {
  const router = useRouter()
  const { asPath } = router
  const prevPathRef = useRef<string>(null)
  const { rudderStackLoaded, analyticsConsentGranted } = useRudderStackInit()
  useHeapInit(analyticsConsentGranted)
  useOsanoConsentEvents(analyticsConsentGranted, rudderStackLoaded)
  useReloadOnConsentRevocation()

  // Capture marketing attribution and shift PREVIOUS/CURRENT router history on
  // navigation only. Must not depend on rudderStackLoaded — that flip re-fires
  // on the same page and would overwrite PREVIOUS_ROUTER with CURRENT.
  useEffect(() => {
    captureFromCurrentUrl()
    reapplyMarketingParamsToUrl()
    storePreviousRouterQuery(router)
  }, [asPath, router])

  // Gate page-view tracking on rudderStackLoaded so prevPathRef is not
  // advanced before analytics is ready (which would drop the first page view).
  useEffect(() => {
    if (rudderStackLoaded) {
      const basePath = asPath.split('?')[0]

      if (basePath !== prevPathRef.current) {
        prevPathRef.current = basePath
        logPageView(basePath)
      }
    }
  }, [asPath, rudderStackLoaded])

  return (
    <UserContextWrapper dispatch={store.dispatch}>
      <DatadogIdentity />
      <QueryClientProvider client={queryClient}>
        <RudderStackContext.Provider
          value={{ rudderStackLoaded, analyticsConsentGranted }}
        >
          <main
            style={{ width: '100%', height: '100%' }}
            className={`${nextFontVariables} ~font-roboto`}
          >
            <Component {...pageProps} />
            <ProviderWaitingNotifications />
            <div id={MODAL_PORTAL_ROOT_ID} />
          </main>
        </RudderStackContext.Provider>
      </QueryClientProvider>
    </UserContextWrapper>
  )
}

// This default export is required in a new `pages/_app.js` file.
function MyApp({ Component, pageProps }: AppProps): JSX.Element {
  // calling useToast hook because, the SuccessBanner & ErrorBanner comp styles were not imported for few SSR pages
  // This issue occurs in build mode (not in dev mode)
  useToast()

  const isCallRailSwapScriptActive =
    useGetTreatment(TreatmentFlags.USE_CALLRAIL_JS) === treatmentFlagValues.ON

  // Report this iframe's location up to an embedding Salesforce parent so the
  // Salesforce "Pop Out" button opens the exact view the agent is on (e.g.
  // provider search), bound to the same client. No-op outside that iframe.
  useEffect(() => {
    return startSalesforceIframeLocationBridge()
  }, [])

  const isErrorTrackingEnabled =
    !!process.env.NEXT_PUBLIC_DATADOG_APP_ID &&
    !!process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN

  return (
    <>
      <Head>
        <link
          rel="apple-touch-icon"
          sizes="180x180"
          href="/images/favicon/apple-touch-icon.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="32x32"
          href="/images/favicon/favicon-32x32.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="16x16"
          href="/images/favicon/favicon-16x16.png"
        />
        <link rel="manifest" href="/site.webmanifest" />
        <link
          rel="mask-icon"
          href="/images/favicon/safari-pinned-tab.svg"
          color="#eaaf19"
        />
        <meta name="msapplication-TileColor" content="#2b5797" />
        <meta name="theme-color" content="#e7e5cb" />
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=2"
        />

        {/* Preload the two self-hosted fonts most likely to be LCP candidates */}
        <link
          rel="preload"
          as="font"
          type="font/woff2"
          href="/fonts/mrseavesxl/bold/mrseavesxlserot-bold-webfont.woff2"
          crossOrigin="anonymous"
        />
        <link
          rel="preload"
          as="font"
          type="font/woff2"
          href="/fonts/gibson/regular/gibson-regular-webfont.woff2"
          crossOrigin="anonymous"
        />

        {process.env.NEXT_PUBLIC_HEAP_APP_ID && (
          // defer: runs after HTML is parsed but before DOMContentLoaded, which is before
          // React's useEffect (where heap.load() is called). Safe to defer since heapInit.js
          // only defines a stub object — it does not need to block HTML parsing.
          <script defer type="text/javascript" src="/static/heapInit.js" />
        )}
      </Head>

      <Datadog>
        <ErrorFallback shouldLogError={isErrorTrackingEnabled}>
          <ReduxProviders>
            <AuthContextWrapper>
              <OsanoCmpProviderSyncedToAuth>
                <PagesAnalyticsShell
                  Component={Component}
                  pageProps={pageProps}
                />
              </OsanoCmpProviderSyncedToAuth>
            </AuthContextWrapper>
            <ToastContainer hideProgressBar />
            <Toaster richColors />
          </ReduxProviders>
        </ErrorFallback>
      </Datadog>

      {/* Script component allows the script tag to be added at the last of the body tag */}
      {isCallRailSwapScriptActive && (
        <Script async type="text/javascript" src={CALLRAIL_SWAP_SCRIPT_SRC} />
      )}
    </>
  )
}

export default MyApp
